import { createContext, useContext, useState, useEffect, useCallback } from "react";
import axios from "axios";

const AuthContext = createContext(null);

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export const AuthProvider = ({ children }) => {
  const [agent, setAgent] = useState(null);
  const [loading, setLoading] = useState(true);
  const [token, setToken] = useState(localStorage.getItem("token"));

  useEffect(() => {
    const initAuth = async () => {
      const storedToken = localStorage.getItem("token");
      if (storedToken) {
        try {
          const response = await axios.get(`${API}/auth/me`, {
            headers: { Authorization: `Bearer ${storedToken}` }
          });
          setAgent(response.data);
          setToken(storedToken);
        } catch (error) {
          // Only evict the token on a genuine auth rejection (401).
          // Network errors and server errors (5xx) leave the token intact so
          // a transient backend blip doesn't force the agent to re-login.
          if (error.response?.status === 401) {
            localStorage.removeItem("token");
            setToken(null);
            setAgent(null);
          }
        }
      }
      setLoading(false);
    };

    initAuth();
  }, []);

  const loginWithToken = (newToken, agentData) => {
    localStorage.setItem("token", newToken);
    setToken(newToken);
    setAgent(agentData);
  };

  const login = async (email, password) => {
    const response = await axios.post(`${API}/auth/login`, { email, password });
    const { token: newToken, agent: agentData } = response.data;
    
    localStorage.setItem("token", newToken);
    setToken(newToken);
    setAgent(agentData);
    
    return agentData;
  };

  const register = async (email, password, name, invite_code) => {
    const response = await axios.post(`${API}/auth/register`, { email, password, name, invite_code });
    const { token: newToken, agent: agentData } = response.data;
    
    localStorage.setItem("token", newToken);
    setToken(newToken);
    setAgent(agentData);
    
    return agentData;
  };

  const logout = () => {
    localStorage.removeItem("token");
    setToken(null);
    setAgent(null);
  };

  const getAuthHeaders = useCallback(() => ({
    Authorization: `Bearer ${token}`
  }), [token]);

  return (
    <AuthContext.Provider value={{
      agent,
      token,
      loading,
      isAuthenticated: !!token && !!agent,
      login,
      loginWithToken,
      register,
      logout,
      getAuthHeaders
    }}>
      {children}
    </AuthContext.Provider>
  );
};

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error("useAuth must be used within an AuthProvider");
  }
  return context;
};
